import { useMediaQuery } from '@mui/material';
import { isToday } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createStorePerformanceRequest, StorePerformanceInterval } from '../../constants';
import { SortDirection } from '../../global-state/types';
import { ProductIndexItem, useAsyncAction, useSoldProducts } from '../../hooks';
import { Container, Icons, Typography } from '../../stories/atoms';
import {
  Animation,
  Animations,
  DropdownMenu,
  DropdownMenuItem,
  FilterTag,
  FilterTagsWidget,
  IconWithText,
} from '../../stories/molecules';
import { Page } from '../../stories/organisms';
import { muiTheme } from '../../theme';
import { homeTranslations } from '../../translations';
import { productCategoryTranslations } from '../../translations/productCategory';
import { getDatePickerLabel, getUniqueListItems, isFailureResponse } from '../../utils';
import { usePageEventTracker } from '../../hooks/usePageEventTracker';
import { HomeDatePicker } from '../home/HomeDatePicker';
import { GarmentGroupView } from './GarmentGroupView/GarmentGroupView';
import { NestedGroupView } from './NestedGroupView';
import {
  ExpandButtonContainer,
  FilterHeaderContainer,
  NestedGroupViewContainer,
  TableFooterContainer,
} from './ProductCategory.styles';
import { ProductCategoryMobileHeader } from './ProductCategoryMobileHeader/ProductCategoryMobileHeader';
import { TableFooter } from './tableComponents/TableFooter';
import { TableHeader } from './tableComponents/TableHeader';
import { TableItem } from './TableItem/TableItem';

export type GroupingKey = 'customerGroup' | 'indexDescription' | 'subindex';

type CategoryKeys = {
  [key in GroupingKey]: string;
};

type GroupingKeyValueChainExpandedMap = {
  [key in string]?: boolean;
};

export type ProductItem = CategoryKeys & ProductIndexItem;

type GarmentGroupContentDetails = {
  groupingKey: GroupingKey | null;
  groupingKeyValue: string | null;
};

const getChildKey = (key: GroupingKey): GroupingKey | null => {
  if (key == 'customerGroup') return 'indexDescription';
  if (key == 'indexDescription') return 'subindex';

  return null;
};

const getGroupingKeyValueChainValue = (groupingKeyChain: string[]) => {
  return groupingKeyChain.join('');
};

export const getTotalSalesNet = (productItems: ProductItem[]) => {
  return productItems.reduce((acc, { salesNet }) => acc + salesNet, 0);
};

export const getTotalLastWeek = (productItems: ProductItem[]) => {
  return productItems.reduce((acc, { salesNetLastWeek }) => acc + salesNetLastWeek, 0);
};

export const getTotalLastYear = (productItems: ProductItem[]) => {
  return productItems.reduce((acc, { salesNetLastYear }) => acc + salesNetLastYear, 0);
};

export const getSob = (salesNet: number, totalSalesNet: number) => {
  return totalSalesNet === 0 ? 0 : (salesNet / totalSalesNet) * 100;
};

export const getAggregateProductIndexItems = (productIndexItems: ProductIndexItem[]) => {
  const getKey = (productIndexItem: ProductIndexItem) => {
    const { departmentNumber, departmentName } = productIndexItem;
    return `${departmentNumber}_${departmentName}`;
  };

  const productIndexItemsMap = productIndexItems.reduce<Map<string, ProductIndexItem>>((ac, currentItem) => {
    const key = getKey(currentItem);

    if (!ac.get(key)) {
      ac.set(key, currentItem);
      return ac;
    }

    const existingItem = ac.get(key);

    if (!existingItem) return ac;

    ac.set(key, {
      ...existingItem,
      salesNet: existingItem.salesNet + currentItem.salesNet,
      salesNetLastWeek: existingItem.salesNetLastWeek + currentItem.salesNetLastWeek,
      salesNetLastYear: existingItem.salesNetLastYear + currentItem.salesNetLastYear,
    });

    return ac;
  }, new Map());

  const aggregateProductIndexItems: ProductIndexItem[] = [];

  productIndexItemsMap.forEach((val) => aggregateProductIndexItems.push(val));
  return aggregateProductIndexItems;
};

export const ProductCategoryPage: FC<unknown> = observer(() => {
  usePageEventTracker('product_category');

  const { t } = useTranslation();
  const { common: commonTranslations, datePicker } = homeTranslations;
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

  const { fetchProductIndex } = useSoldProducts();

  const {
    result: productItems,
    isLoading: productItemsLoading,
    isError: productItemsError,
    triggerAction: triggerGetProductItems,
  } = useAsyncAction<ProductItem[]>();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentInterval, setCurrentInterval] = useState<StorePerformanceInterval>(StorePerformanceInterval.DAILY);

  const [dateLabel, setDateLabel] = useState(
    getDatePickerLabel(
      selectedDate,
      currentInterval,
      t(datePicker.weekLabel.key, datePicker.weekLabel.defaultValue),
    ),
  );

  const [sortValue, setSortValue] = useState<SortDirection | null>(null);
  const [filteredIndexDescriptions, setFilteredIndexDescriptions] = useState<string[]>([]);
  const [filteredSubIndexDescriptions, setFilteredSubIndexDescriptions] = useState<string[]>([]);

  const [garmentGroupViewDetails, setGarmentGroupViewDetails] = useState<GarmentGroupContentDetails | null>(null);

  const showGarmentGroupView = (groupingKey: GroupingKey | null, groupingKeyValue: string | null) => {
    if (!productItems || productItems.length === 0) return;

    setGarmentGroupViewDetails({ groupingKey, groupingKeyValue });
  };

  const hideGarmentGroupView = () => {
    setGarmentGroupViewDetails(null);
  };

  const getProductItems = async () => {
    const request = createStorePerformanceRequest({
      interval: currentInterval,
      currentDate: selectedDate,
    });
    const response = await fetchProductIndex(request);

    if (isFailureResponse(response)) {
      throw new Error();
    }

    return getAggregateProductIndexItems(response.data);
  };

  // Handle expand/collapse
  const [groupingKeyValueChainExpandedMap, setGroupingKeyValueChainExpandedMap] =
    useState<GroupingKeyValueChainExpandedMap>({});

  const isAnyExpanded = Object.keys(groupingKeyValueChainExpandedMap).length !== 0;

  const handleToggleExpandGroup = (groupingKeyChain: string[]) => {
    const groupingKeyChainValue = getGroupingKeyValueChainValue(groupingKeyChain);

    const updatedGroupingKeyValueChainExpandedMap = { ...groupingKeyValueChainExpandedMap };

    if (updatedGroupingKeyValueChainExpandedMap[groupingKeyChainValue]) {
      const keys = Object.keys(updatedGroupingKeyValueChainExpandedMap);

      keys.forEach((key) => {
        if (key.indexOf(groupingKeyChainValue) === -1) return;
        delete updatedGroupingKeyValueChainExpandedMap[key];
      });

      setGroupingKeyValueChainExpandedMap(updatedGroupingKeyValueChainExpandedMap);
      return;
    }

    updatedGroupingKeyValueChainExpandedMap[groupingKeyChainValue] = true;
    setGroupingKeyValueChainExpandedMap(updatedGroupingKeyValueChainExpandedMap);
  };

  const getIsGroupExpanded = (groupingKeyChain: string[]) => {
    const groupingKeyValueChainValue = getGroupingKeyValueChainValue(groupingKeyChain);
    return groupingKeyValueChainExpandedMap[groupingKeyValueChainValue] || false;
  };

  const expandAll = () => {
    if (!productItems) return;

    const newGroupingKeyValueChainExpandedMap: GroupingKeyValueChainExpandedMap = {};

    productItems.forEach((item) => {
      let groupingKeyValueChain = '';
      let currentGroupingKey: GroupingKey = 'customerGroup';

      while (true) {
        const groupingKeyValue = item[currentGroupingKey];

        if (!groupingKeyValue) break;

        groupingKeyValueChain += groupingKeyValue;

        newGroupingKeyValueChainExpandedMap[groupingKeyValueChain] = true;

        const nextGroupingKey = getChildKey(currentGroupingKey);

        if (!nextGroupingKey) break;

        currentGroupingKey = nextGroupingKey;
      }
    });

    setGroupingKeyValueChainExpandedMap(newGroupingKeyValueChainExpandedMap);
  };

  const collapseAll = () => {
    setGroupingKeyValueChainExpandedMap({});
  };

  // Handle Sorting
  const sortButtonHandler = () => {
    setSortValue((currentSortValue) => {
      if (currentSortValue === null) return SortDirection.Descending;
      if (currentSortValue === SortDirection.Descending) return SortDirection.Ascending;
      return SortDirection.Descending;
    });
  };

  const groupSortFunction = (groupA: ProductItem[], groupB: ProductItem[]) => {
    if (!sortValue) return 0;

    const totalNetSalesA = groupA.reduce((acc, { salesNet }) => acc + salesNet, 0);
    const totalNetSalesB = groupB.reduce((acc, { salesNet }) => acc + salesNet, 0);

    if (sortValue === SortDirection.Descending) {
      if (totalNetSalesA < totalNetSalesB) return 1;
      if (totalNetSalesA > totalNetSalesB) return -1;
    }

    if (totalNetSalesA > totalNetSalesB) return 1;
    if (totalNetSalesA < totalNetSalesB) return -1;

    return 0;
  };

  const itemSortFunction = (a: ProductItem, b: ProductItem) => {
    if (!sortValue) return 0;

    if (sortValue === SortDirection.Descending) {
      if (a.salesNet < b.salesNet) return 1;
      if (a.salesNet > b.salesNet) return -1;
    }

    if (a.salesNet > b.salesNet) return 1;
    if (a.salesNet < b.salesNet) return -1;

    return 0;
  };

  // Date picker handlers
  const handleDateChange = (date: Date, interval: StorePerformanceInterval) => {
    setSelectedDate(date);
    setCurrentInterval(interval);
    setDateLabel(
      getDatePickerLabel(date, interval, t(datePicker.weekLabel.key, datePicker.weekLabel.defaultValue)),
    );
  };

  // Filter widget handlers
  const handleDateFilterTagClick = () => {
    handleDateChange(new Date(), StorePerformanceInterval.DAILY);
  };

  const handleIndexFilterTagClick = (key: string) => {
    const keyIndex = filteredIndexDescriptions.indexOf(key);

    const updatedFilteredIndexDescriptions = [...filteredIndexDescriptions];
    updatedFilteredIndexDescriptions.splice(keyIndex, 1);
    setFilteredIndexDescriptions(updatedFilteredIndexDescriptions);
  };

  const handleSubIndexFilterTagClick = (key: string) => {
    const keyIndex = filteredSubIndexDescriptions.indexOf(key);

    const updatedFilteredSubIndexDescriptions = [...filteredSubIndexDescriptions];
    updatedFilteredSubIndexDescriptions.splice(keyIndex, 1);
    setFilteredSubIndexDescriptions(updatedFilteredSubIndexDescriptions);
  };

  const clearAllFilters = (resetDateFilter: boolean = true) => {
    // Don't reset the currentDates values if the default is being used
    if (resetDateFilter) {
      handleDateChange(new Date(), StorePerformanceInterval.DAILY);
    }

    setFilteredIndexDescriptions([]);
    setFilteredSubIndexDescriptions([]);
  };

  const handleSettings = (selectedKeys: string[]) => {
    if (selectedKeys.length === 0) return;

    if (selectedKeys[0] === 'showGarmentGroups') {
      showGarmentGroupView(null, null);
    } else if (isAnyExpanded) {
      collapseAll();
    } else {
      expandAll();
    }
  };

  const updateSubIndexOnIndexFilters = (
    currentFilteredIndexDescriptions: string[],
    currentFilteredSubIndexDescriptions: string[],
    currentProductItems: ProductItem[] | null,
  ) => {
    if (!currentProductItems) return;
    if (currentFilteredIndexDescriptions.length === 0) return;
    if (currentFilteredSubIndexDescriptions.length === 0) return;

    // we need to filter out sub index filters that don't exist on current selected index filters

    const indexProductItems = currentProductItems.filter(
      (item) => currentFilteredIndexDescriptions.indexOf(item.indexDescription) !== -1,
    );

    const updatedFilteredSubIndexDescriptions = currentFilteredSubIndexDescriptions.filter(
      (subIndexDescription) => {
        return indexProductItems.some((item) => item.subindex === subIndexDescription);
      },
    );

    setFilteredSubIndexDescriptions(updatedFilteredSubIndexDescriptions);
  };

  useEffect(() => {
    clearAllFilters(false);
    collapseAll();
    triggerGetProductItems(getProductItems);
  }, [selectedDate]);

  useEffect(() => {
    updateSubIndexOnIndexFilters(filteredIndexDescriptions, filteredSubIndexDescriptions, productItems);
  }, [filteredIndexDescriptions, productItems]);

  const filteredProductItems: ProductItem[] = !productItems
    ? []
    : productItems.filter((item) => {
        const { indexDescription, subindex } = item;

        const isIndexFilterEnabled = filteredIndexDescriptions.length !== 0;
        const isSubIndexFilterEnabled = filteredSubIndexDescriptions.length !== 0;

        // if no filter is enabled return all items
        if (!isIndexFilterEnabled && !isSubIndexFilterEnabled) return true;

        const isIndexMatch = filteredIndexDescriptions.includes(indexDescription);
        const isSubIndexMatch = filteredSubIndexDescriptions.includes(subindex);

        if (isIndexFilterEnabled && !isSubIndexFilterEnabled) {
          return isIndexMatch;
        }

        if (isSubIndexFilterEnabled && !isIndexFilterEnabled) {
          return isSubIndexMatch;
        }

        return isIndexMatch && isSubIndexMatch;
      });

  const indexDropdownMenuItems: DropdownMenuItem[] = !productItems
    ? []
    : getUniqueListItems(productItems, (item) => item.indexDescription)
        .sort((a, b) => {
          if (a.indexDescription > b.indexDescription) return 1;
          if (a.indexDescription < b.indexDescription) return -1;
          return 0;
        })
        .map((item) => {
          return {
            key: item.indexDescription,
            item: <Typography margin={[0, 2, 0]}>{item.indexDescription}</Typography>,
          };
        });

  const subIndexDropdownMenuItems: DropdownMenuItem[] = !productItems
    ? []
    : getUniqueListItems(productItems, (item) => item.subindex)
        .sort((a, b) => {
          if (a.subindex > b.subindex) return 1;
          if (a.subindex < b.subindex) return -1;
          return 0;
        })
        .map((item) => {
          let isDisabled = true;

          if (filteredIndexDescriptions.length === 0) isDisabled = false;

          if (filteredIndexDescriptions.length !== 0) {
            isDisabled = !filteredIndexDescriptions.some((description) => description === item.indexDescription);
          }

          return {
            key: item.subindex,
            item: <Typography margin={[0, 2, 0]}>{item.subindex}</Typography>,
            disabled: isDisabled,
          };
        });

  const isNoData = productItems && productItems.length === 0;

  const totalSalesNet = !productItems ? 0 : getTotalSalesNet(productItems);
  const totalSalesNetFiltered = getTotalSalesNet(filteredProductItems);
  const totalSalesNetLastWeek = !productItems ? 0 : getTotalLastWeek(productItems);
  const totalSalesNetLastYear = !productItems ? 0 : getTotalLastYear(productItems);

  const isDateRangeFilterActive = !isToday(selectedDate);
  const isIndexFilterActive = filteredIndexDescriptions.length > 0;
  const isSubIndexFilterActive = filteredSubIndexDescriptions.length > 0;

  const isAnyFilterActive = isDateRangeFilterActive || isIndexFilterActive || isSubIndexFilterActive;

  return (
    <Page
      header={
        garmentGroupViewDetails ? undefined : (
          <FilterHeaderContainer isMobile={isMobile}>
            <HomeDatePicker
              currentDate={selectedDate}
              dateLabel={dateLabel}
              onDateChange={handleDateChange}
              showMonthyPicker
              isDatePickerDisabled={productItemsLoading}
            />
            {!isMobile && (
              <>
                <DropdownMenu
                  dropdownLabel={t(
                    productCategoryTranslations.filter.index.key,
                    productCategoryTranslations.filter.index.defaultValue,
                  )}
                  menuPosition="left"
                  maxHeight={'50vh'}
                  menuItems={indexDropdownMenuItems}
                  checkbox={true}
                  multiSelect
                  initialSelected={filteredIndexDescriptions}
                  buttonContentProps={{ isDisabled: indexDropdownMenuItems.length === 0 }}
                  onSelect={(selectedItemKeys) => setFilteredIndexDescriptions(selectedItemKeys)}
                />

                <DropdownMenu
                  dropdownLabel={t(
                    productCategoryTranslations.filter.subIndex.key,
                    productCategoryTranslations.filter.subIndex.defaultValue,
                  )}
                  menuPosition="left"
                  maxHeight={'50vh'}
                  menuItems={subIndexDropdownMenuItems}
                  checkbox={true}
                  multiSelect
                  initialSelected={filteredSubIndexDescriptions}
                  buttonContentProps={{ isDisabled: subIndexDropdownMenuItems.length === 0 }}
                  onSelect={(selectedItemKeys) => setFilteredSubIndexDescriptions(selectedItemKeys)}
                />
              </>
            )}

            {isMobile && (
              <ProductCategoryMobileHeader
                filteredProductItems={filteredProductItems}
                indexDropdownMenuItems={indexDropdownMenuItems}
                subIndexDropdownMenuItems={subIndexDropdownMenuItems}
                filteredIndexDescriptions={filteredIndexDescriptions}
                filteredSubIndexDescriptions={filteredSubIndexDescriptions}
                isAnyExpanded={isAnyExpanded}
                isNoData={isNoData ?? false}
                sortValue={sortValue}
                handleSortValue={(key) => setSortValue(key)}
                clearAllFilters={clearAllFilters}
                onSettingsMenuSelect={(selectedKeys) => handleSettings(selectedKeys)}
                updateFilteredItems={(selectedItems, itemType) => {
                  if (itemType === 'index') {
                    setFilteredIndexDescriptions(selectedItems);
                  } else {
                    setFilteredSubIndexDescriptions(selectedItems);
                  }
                }}
              />
            )}

            {!isMobile && (
              <ExpandButtonContainer>
                <IconWithText
                  label={
                    isMobile
                      ? undefined
                      : t(
                          productCategoryTranslations.garmentGroup.viewAllbutton.key,
                          productCategoryTranslations.garmentGroup.viewAllbutton.defaultValue,
                        )
                  }
                  iconOptions={{
                    icon: Icons.ALLWIDGETS,
                    size: 'small',
                    rotate: 'up',
                  }}
                  textOptions={{
                    type: 'button',
                  }}
                  onClick={() => showGarmentGroupView(null, null)}
                />

                <IconWithText
                  label={
                    isAnyExpanded
                      ? t(commonTranslations.collapseAll.key, commonTranslations.collapseAll.defaultValue)
                      : t(commonTranslations.expandAll.key, commonTranslations.expandAll.defaultValue)
                  }
                  iconOptions={{
                    icon: isAnyExpanded ? Icons.CHECKBOX_INDETERMINATE : Icons.PLUS,
                    size: 'small',
                  }}
                  textOptions={{
                    type: 'button',
                  }}
                  onClick={isAnyExpanded ? collapseAll : expandAll}
                  padding={[0, 0, 0, 8]}
                />
              </ExpandButtonContainer>
            )}
          </FilterHeaderContainer>
        )
      }
      body={
        <div>
          {garmentGroupViewDetails && (
            <Container
              wrap="nowrap"
              padding={isMobile ? [4, 5] : [4, 8]}
              height="100%"
              style={{ position: 'relative' }}
            >
              {productItems && garmentGroupViewDetails && (
                <GarmentGroupView
                  productItems={productItems}
                  groupingKey={garmentGroupViewDetails.groupingKey}
                  groupingKeyValue={garmentGroupViewDetails.groupingKeyValue}
                  backButtonHandler={hideGarmentGroupView}
                  interval={StorePerformanceInterval.DAILY}
                  currentDate={new Date()}
                />
              )}
            </Container>
          )}

          {!garmentGroupViewDetails && (
            <Container
              wrap="nowrap"
              padding={isMobile ? [4, 5] : [4, 8]}
              height="100%"
              style={{ position: 'relative' }}
            >
              {productItemsLoading && (
                <Animation
                  title={t(
                    productCategoryTranslations.loading.title.key,
                    productCategoryTranslations.loading.title.defaultValue,
                  )}
                  animation={Animations.LOADING}
                />
              )}

              {!productItemsLoading && productItemsError && (
                <Animation
                  title={t(
                    productCategoryTranslations.error.title.key,
                    productCategoryTranslations.error.title.defaultValue,
                  )}
                  subtitle={t(
                    productCategoryTranslations.error.subTitle.key,
                    productCategoryTranslations.error.subTitle.defaultValue,
                  )}
                  animation={Animations.SAD}
                />
              )}

              {!productItemsLoading && !productItemsError && isNoData && (
                <>
                  <Typography padding={[2, 0]} type="body3">
                    {t(
                      productCategoryTranslations.datePrefix.key,
                      productCategoryTranslations.datePrefix.defaultValue,
                    )}{' '}
                    {dateLabel}
                  </Typography>

                  <Animation
                    title={t(
                      productCategoryTranslations.noData.title.key,
                      productCategoryTranslations.noData.title.defaultValue,
                    )}
                    subtitle={t(
                      productCategoryTranslations.noData.subtitle.key,
                      productCategoryTranslations.noData.subtitle.defaultValue,
                    )}
                    animation={Animations.CONFUSED}
                  />
                </>
              )}

              {!productItemsLoading && !productItemsError && productItems && productItems.length > 0 && (
                <>
                  {!isMobile && (
                    <FilterTagsWidget
                      isClearButtonVisible={isAnyFilterActive}
                      clearButtonText={t(
                        productCategoryTranslations.filter.clearAllFilters.key,
                        productCategoryTranslations.filter.clearAllFilters.defaultValue,
                      )}
                      clearButtonHandler={clearAllFilters}
                      filterTags={[
                        <Typography padding={[2, 0]} type="body3">
                          {t(
                            productCategoryTranslations.datePrefix.key,
                            productCategoryTranslations.datePrefix.defaultValue,
                          )}
                        </Typography>,
                        isDateRangeFilterActive ? (
                          <FilterTag key={dateLabel} title={dateLabel} onClick={handleDateFilterTagClick} />
                        ) : (
                          <Typography padding={[2, 0]} type="body3">
                            {dateLabel}
                          </Typography>
                        ),
                        ...filteredIndexDescriptions.map((description) => (
                          <FilterTag
                            key={description}
                            title={description}
                            onClick={() => handleIndexFilterTagClick(description)}
                          />
                        )),
                        ...filteredSubIndexDescriptions.map((description) => (
                          <FilterTag
                            key={description}
                            title={description}
                            onClick={() => handleSubIndexFilterTagClick(description)}
                          />
                        )),
                      ]}
                    />
                  )}

                  {!isMobile && <TableHeader sortType={sortValue} sortButtonHandler={sortButtonHandler} />}

                  <NestedGroupViewContainer data-testid="nested-group-view-container" isMobileView={isMobile}>
                    <NestedGroupView
                      groupingKey="customerGroup"
                      depth={0}
                      items={filteredProductItems}
                      getNextChildKey={getChildKey}
                      groupItemRenderer={(groupingKey, items, depth, groupingKeyValueChain) => {
                        const item = items[0];
                        const groupingKeyValue = item[groupingKey];

                        const salesNet = items.reduce((acc, x) => acc + x.salesNet, 0);
                        const lastWeek = items.reduce((acc, x) => acc + x.salesNetLastWeek, 0);
                        const lastYear = items.reduce((acc, x) => acc + x.salesNetLastYear, 0);

                        return (
                          <TableItem
                            depth={depth}
                            isParent={true}
                            isExpanded={getIsGroupExpanded(groupingKeyValueChain)}
                            title={item[groupingKey]}
                            salesNet={salesNet}
                            lastWeek={lastWeek}
                            lastYear={lastYear}
                            sob={getSob(salesNet, totalSalesNet)}
                            expandButtonHandler={() => handleToggleExpandGroup(groupingKeyValueChain)}
                            garmentGroupButtonHandler={() => showGarmentGroupView(groupingKey, groupingKeyValue)}
                            interval={currentInterval}
                            currentDate={selectedDate}
                          />
                        );
                      }}
                      itemRenderer={(item, depth) => {
                        const {
                          id,
                          departmentNumber,
                          departmentName,
                          salesNet,
                          salesNetLastWeek,
                          salesNetLastYear,
                        } = item;

                        const title = `${departmentNumber} - ${departmentName}`;

                        return (
                          <TableItem
                            key={id}
                            depth={depth}
                            isParent={false}
                            isExpanded={false}
                            title={title}
                            salesNet={salesNet}
                            lastWeek={salesNetLastWeek}
                            lastYear={salesNetLastYear}
                            sob={getSob(salesNet, totalSalesNet)}
                            interval={currentInterval}
                            currentDate={selectedDate}
                          />
                        );
                      }}
                      getChildrenContainerStyle={(groupingKeyValueChain) => {
                        const isExpanded = getIsGroupExpanded(groupingKeyValueChain);

                        return {
                          height: isExpanded ? 'auto' : '0',
                          overflow: 'hidden',
                        };
                      }}
                      groupSortFunction={groupSortFunction}
                      itemSortFunction={itemSortFunction}
                    />
                  </NestedGroupViewContainer>

                  <TableFooterContainer>
                    <TableFooter
                      totalSalesNet={totalSalesNet}
                      totalSalesNetFiltered={totalSalesNetFiltered}
                      salesNetLastWeek={totalSalesNetLastWeek}
                      salesNetLastYear={totalSalesNetLastYear}
                    />
                  </TableFooterContainer>
                </>
              )}
            </Container>
          )}
        </div>
      }
    />
  );
});
